import { FifoCache } from '../utility/FifoCache';

export class ColorModel {
  r: number;
  g: number;
  b: number;
  a: number;

  public static readonly defaultAlpha = 1.0;
  public static readonly white = new ColorModel(255, 255, 255, ColorModel.defaultAlpha);
  public static readonly black = new ColorModel(0, 0, 0, ColorModel.defaultAlpha);
  // The minimum luminance for a color to be considered light.
  public static readonly luminanceThresholdForLightColor = 0.315;
  public static readonly WhiteHoverColor: ColorModel = new ColorModel(255, 255, 255, 0.45);
  public static readonly BlackHoverColor: ColorModel = new ColorModel(0, 0, 0, 0.45);

  private rgbStr: string; //colorString
  private static parsedColorStrCache = new FifoCache<ColorModel>(100);
  private static readonly hexFormat = /#([0-9A-F]{2})([0-9A-F]{2})([0-9A-F]{2})/i;
  private static readonly rbgFormat = /rgb\((\d+), ?(\d+), ?(\d+)\)/i;
  private static readonly rbgaFormat = /rgba\((\d+), ?(\d+), ?(\d+), ?(\d+|0\.\d+|1\.0)\)/i;

  // This lookup table replaces "pow(i/255.0, 2.2)"
  private static readonly pow2point2: number[] = [
    0.0, 0.00000507705190066, 0.0000233280046661, 0.0000569217657122, 0.000107187362341, 0.000175123977503, 0.000261543754548,
    0.000367136269816, 0.000492503787191, 0.000638182842167, 0.000804658499513, 0.000992374304074, 0.00120173952244, 0.00143313458967,
    0.00168691531679, 0.0019634162134, 0.00226295316071, 0.00258582559623, 0.00293231832394, 0.003302703032, 0.0036972395789,
    0.00411617709328, 0.00455975492253, 0.00502820345686, 0.00552174485024, 0.00604059365485, 0.00658495738258, 0.00715503700457,
    0.00775102739766, 0.00837311774515, 0.00902149189801, 0.00969632870166, 0.0103978022926, 0.0111260823684, 0.0118813344348,
    0.0126637200316, 0.0134733969401, 0.0143105193749, 0.0151752381596, 0.0160677008909, 0.0169880520893, 0.01793643334, 0.0189129834237,
    0.0199178384388, 0.0209511319148, 0.0220129949193, 0.0231035561579, 0.0242229420675, 0.0253712769047, 0.0265486828285, 0.0277552799781,
    0.0289911865471, 0.0302565188524, 0.0315513914002, 0.0328759169484, 0.0342302065651, 0.0356143696849, 0.037028514162, 0.0384727463202,
    0.0399471710015, 0.0414518916115, 0.0429870101627, 0.0445526273164, 0.0461488424224, 0.0477757535562, 0.0494334575559, 0.0511220500565,
    0.0528416255229, 0.0545922772818, 0.056374097552, 0.0581871774737, 0.0600316071363, 0.0619074756055, 0.0638148709487, 0.0657538802603,
    0.0677245896854, 0.0697270844426, 0.0717614488462, 0.0738277663278, 0.0759261194563, 0.0780565899581, 0.0802192587362, 0.0824142058885,
    0.0846415107254, 0.0869012517877, 0.0891935068622, 0.0915183529989, 0.0938758665256, 0.0962661230633, 0.0986891975411, 0.10114516421,
    0.103634096655, 0.106156067813, 0.108711149979, 0.111299414825, 0.113920933406, 0.116575776179, 0.119264013005, 0.12198571317,
    0.124740945387, 0.127529777813, 0.130352278056, 0.133208513184, 0.136098549737, 0.139022453735, 0.141980290686, 0.144972125597,
    0.147998022983, 0.151058046871, 0.154152260812, 0.15728072789, 0.160443510725, 0.163640671485, 0.166872271891, 0.170138373223,
    0.173439036332, 0.176774321641, 0.180144289154, 0.183548998465, 0.186988508759, 0.190462878822, 0.193972167048, 0.19751643144,
    0.201095729621, 0.204710118837, 0.208359655961, 0.212044397502, 0.215764399609, 0.219519718075, 0.223310408341, 0.227136525505,
    0.230998124323, 0.234895259216, 0.238827984272, 0.242796353254, 0.246800419602, 0.250840236436, 0.254915856566, 0.25902733249,
    0.263174716398, 0.267358060184, 0.271577415438, 0.275832833461, 0.280124365261, 0.28445206156, 0.288815972797, 0.293216149132,
    0.297652640449, 0.302125496359, 0.306634766203, 0.311180499058, 0.315762743736, 0.320381548792, 0.325036962521, 0.329729032968,
    0.334457807924, 0.339223334935, 0.344025661302, 0.348864834083, 0.353740900097, 0.358653905926, 0.363603897921, 0.368590922197,
    0.373615024646, 0.37867625093, 0.383774646488, 0.388910256539, 0.394083126083, 0.399293299903, 0.404540822568, 0.409825738436,
    0.415148091656, 0.420507926168, 0.425905285707, 0.431340213807, 0.4368127538, 0.442322948819, 0.4478708418, 0.453456475486,
    0.459079892424, 0.464741134974, 0.470440245304, 0.476177265397, 0.481952237051, 0.487765201878, 0.493616201311, 0.499505276603,
    0.505432468828, 0.511397818885, 0.517401367497, 0.523443155214, 0.529523222417, 0.535641609315, 0.54179835595, 0.547993502197,
    0.554227087766, 0.560499152204, 0.566809734897, 0.573158875068, 0.579546611783, 0.58597298395, 0.592438030321, 0.598941789493,
    0.605484299911, 0.612065599866, 0.618685727499, 0.625344720802, 0.632042617621, 0.638779455651, 0.645555272445, 0.652370105411,
    0.659223991813, 0.666116968776, 0.673049073281, 0.680020342172, 0.687030812155, 0.694080519797, 0.701169501531, 0.708297793656,
    0.715465432335, 0.7226724536, 0.729918893352, 0.737204787361, 0.744530171267, 0.751895080583, 0.759299550695, 0.766743616862,
    0.774227314218, 0.781750677774, 0.789313742416, 0.796916542908, 0.804559113895, 0.812241489898, 0.819963705324, 0.827725794455,
    0.835527791461, 0.843369730392, 0.851251645185, 0.859173569659, 0.867135537521, 0.875137582365, 0.883179737673, 0.891262036813,
    0.899384513047, 0.907547199522, 0.915750129279, 0.923993335252, 0.932276850265, 0.940600707036, 0.948964938178, 0.9573695762,
    0.965814653503, 0.974300202389, 0.982826255054, 0.991392843593, 1.0,
  ];

  constructor(r: number, g: number, b: number, a: number) {
    this.r = r;
    this.g = g;
    this.b = b;
    this.a = a;

    this.rgbStr = '';
  }

  private static getBoundedColor(i: number): number {
    return Math.min(Math.max(i, 0), 255);
  }

  public static fromColorCode(colorCode: string): ColorModel {
    // check if already parsed/cached. If so, return cached value
    const cachedColor: ColorModel | undefined = this.parsedColorStrCache.get(colorCode);
    if (cachedColor) {
      return cachedColor;
    }

    let matches: RegExpExecArray | null = this.hexFormat.exec(colorCode);
    if (matches) {
      const retVal = ColorModel.createRGBColorModelFromMatches(matches, 16);
      this.parsedColorStrCache.add(colorCode, retVal);
      return retVal;
    }

    matches = this.rbgFormat.exec(colorCode);
    if (matches) {
      const retVal = ColorModel.createRGBColorModelFromMatches(matches, 10);
      retVal.setRGBStr(colorCode);
      this.parsedColorStrCache.add(colorCode, retVal);
      return retVal;
    }

    matches = this.rbgaFormat.exec(colorCode);
    if (matches) {
      return ColorModel.createRGBAColorModelFromMatches(matches, 10, colorCode);
    }

    throw new Error('Unknown color format: ' + colorCode.toString());
  }

  protected static createRGBColorModelFromMatches(matches: RegExpExecArray, intBase: number): ColorModel {
    const r = ColorModel.getBoundedColor(parseInt(matches[1], intBase));
    const g = ColorModel.getBoundedColor(parseInt(matches[2], intBase));
    const b = ColorModel.getBoundedColor(parseInt(matches[3], intBase));
    const a = ColorModel.defaultAlpha;

    return new ColorModel(r, g, b, a);
  }

  protected static createRGBAColorModelFromMatches(matches: RegExpExecArray, intBase: number, colorCode: string): ColorModel {
    const colorModel = this.createRGBColorModelFromMatches(matches, intBase);
    let a = parseFloat(matches[4]);
    const r = colorModel.r;
    const g = colorModel.g;
    const b = colorModel.b;

    // BUG 145849: We are expecting alpha to be in [0,255]. However, if the string has been coverted by ConvertColorForCanvas, alpha is already in [0,1].
    // There is a problem that if a = 1, we don't know if it is converted. That is, is it 1 in [0,1] or 1 in [0,255].
    // Here I treat it as converted, because on desktop we are picking a percentage. 1% would be 2/255 or 3/255. We do not allow non-integer percentage like 0.3%. Therefore 1/255 is unlikely to happen.
    if (a <= 1.0) {
      a = a * 255.0;
    }
    if (a > 255) {
      a = 255;
    }
    if (a < 0) {
      a = 0;
    }
    a = ColorModel.alpha255ToFraction(a);

    // store result in cache for future re-use
    const retVal = new ColorModel(r, g, b, a);
    ColorModel.parsedColorStrCache.add(colorCode, retVal);
    return retVal;
  }

  protected setRGBStr(str: string): void {
    this.rgbStr = str;
  }

  public static alpha255ToFraction(num: number): number {
    return ColorModel.truncateTwoDecimalPlaces(ColorModel.untruncatedAlpha255ToFraction(num));
  }

  public static truncateTwoDecimalPlaces(num: number): number {
    return Math.trunc((num * 1000) / 10) / 100.0;
  }

  public static untruncatedAlpha255ToFraction(num: number): number {
    return (num + 1) / 256.0;
  }

  public toRGBA(): string {
    return 'rgba(' + this.r.toString() + ',' + this.g.toString() + ',' + this.b.toString() + ',' + this.a.toString() + ')';
  }

  public toRGB(): string {
    if (this.rgbStr === '') {
      return 'rgb(' + this.r.toString() + ',' + this.g.toString() + ',' + this.b.toString() + ')';
    }
    return this.rgbStr;
  }

  public isLuminanceAboveThreshold(luminanceThreshold: number = ColorModel.luminanceThresholdForLightColor): boolean {
    return this.luminance() > luminanceThreshold;
  }

  // Return the Luminance value of the color as a float between 0(dark) and 1(light).
  // Note1: This is computationally intensive and not intended for bulk transformation.
  // Note2: The formula matches the method GetLuminance in ColorUtil.cpp.
  // Note3: Luminance is preferrable comparing to LStar.
  private luminance(): number {
    return ColorModel.luminance(this.r, this.g, this.b);
  }

  private static luminance(r: number, g: number, b: number): number {
    return 0.2126 * this.pow2point2[r] + 0.7152 * this.pow2point2[g] + 0.0722 * this.pow2point2[b];
  }

  public static alphaBlend(transparentColor: ColorModel, opaqueColor: ColorModel): ColorModel {
    if (transparentColor.a < 1) {
      const weightOfOpaqueColor: number = ColorModel.alphaToTransparency(transparentColor.a);
      const red: number = opaqueColor.r * weightOfOpaqueColor + transparentColor.r * transparentColor.a;
      const green: number = opaqueColor.g * weightOfOpaqueColor + transparentColor.g * transparentColor.a;
      const blue: number = opaqueColor.b * weightOfOpaqueColor + transparentColor.b * transparentColor.a;
      return new ColorModel(red, green, blue, this.defaultAlpha);
    } else {
      return transparentColor;
    }
  }

  // Given a transparency percentage (0.0f = opaque, 1.0f = transparent), convert it to
  // an alpha percentage (0.0f = transparent, 1.0f = opaque).
  public static transparencyToAlpha(transparency: number): number {
    return Math.min(Math.max(1.0 - transparency, 0.0), 1.0);
  }

  // Given an alpha percentage (0.0 = transparent, 1.0 = opaque), convert it to
  // a transparency percentage (0.0 = opaque, 1.0 = transparent).
  public static alphaToTransparency(alpha: number): number {
    // This is the same (1-x) logic as TransparencyToAlpha above - no need to duplicate it
    return ColorModel.transparencyToAlpha(alpha);
  }

  // Initializes <see cref="ColorModel"/> from another ColorModel, injecting a custom alpha value.
  public static fromColorModelPlusAlpha(colorModel: ColorModel, a: number): ColorModel {
    return new ColorModel(colorModel.r, colorModel.g, colorModel.b, a);
  }
}
